import { z } from "zod";
import { assignEliminationReferees } from "./referees";
import {
  configSchema,
  draftSchema,
  ensure,
  idSchema,
  required,
  RuleError,
  type Actor,
  type Draft,
  type Match,
  type Pair,
  type Tournament,
} from "./model";
import {
  descendants,
  elimination,
  makeMatch,
  poolDraw,
  resolveMatches,
} from "./draw";
import {
  kingRound,
  startPlayoff,
  swissRound,
  unresolvedTies,
} from "./standings";
import {
  activeTeamIds,
  assignPoolReferees,
  courtOpen,
  dispatch,
  refereeCandidates,
} from "./schedule";
import { scoringFor, setWinner, validateResult } from "./scoring";

const pairSchema = z.tuple([
  z.number().int().min(0).max(199),
  z.number().int().min(0).max(199),
]);
const target = { matchId: idSchema, revision: z.number().int().nonnegative() };
export const commandSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("save_draft"), draft: draftSchema }),
  z.object({ type: z.literal("publish") }),
  z.object({
    type: z.literal("point"),
    ...target,
    side: z.enum(["a", "b"]),
    delta: z.union([z.literal(1), z.literal(-1)]),
  }),
  z.object({
    type: z.literal("finish"),
    ...target,
    sets: z.array(pairSchema).min(1).max(5),
  }),
  z.object({
    type: z.literal("correct"),
    ...target,
    sets: z.array(pairSchema).min(1).max(5),
    resetDependents: z.boolean(),
    reason: z.string().trim().min(3).max(500),
  }),
  z.object({ type: z.literal("next_set"), ...target }),
  z.object({ type: z.literal("release"), matchId: idSchema }),
  z.object({
    type: z.literal("assign"),
    matchId: idSchema,
    courtId: idSchema,
    refereeMode: z.enum(["auto", "team", "self"]),
    refereeId: idSchema.optional(),
    needsRelease: z.boolean(),
    notBefore: z.iso.datetime({ offset: true }).nullable(),
  }),
  z.object({
    type: z.literal("pool_court"),
    poolId: idSchema,
    courtId: idSchema,
  }),
  z.object({
    type: z.literal("resolve_tie"),
    key: z.string().min(1).max(50000),
    winnerId: idSchema,
  }),
  z.object({ type: z.literal("advance") }),
  z.object({ type: z.literal("start_heat"), heatId: idSchema }),
  z.object({
    type: z.literal("finish_heat"),
    heatId: idSchema,
    revision: z.number().int().nonnegative(),
    totals: z.record(idSchema, z.number().int().min(0).max(10000)),
  }),
  z.object({
    type: z.literal("correct_heat"),
    heatId: idSchema,
    revision: z.number().int().nonnegative(),
    totals: z.record(idSchema, z.number().int().min(0).max(10000)),
    resetDependents: z.boolean(),
    reason: z.string().trim().min(3).max(500),
  }),
  z.object({
    type: z.literal("withdraw"),
    teamId: idSchema,
    replacementId: idSchema.optional(),
    reason: z.string().trim().min(3).max(500),
  }),
  z.object({
    type: z.literal("retire"),
    ...target,
    winnerId: idSchema,
    reason: z.string().trim().min(3).max(500),
  }),
  z.object({
    type: z.literal("manual_swiss"),
    pairs: z
      .array(z.tuple([idSchema, idSchema.nullable()]))
      .min(1)
      .max(25),
  }),
  z.object({ type: z.literal("settings"), config: configSchema }),
]);
export type Command = z.infer<typeof commandSchema>;

function validateDraft(draft: Draft): void {
  const unique = (values: string[]) => new Set(values).size === values.length;
  ensure(unique(draft.teams.map((t) => t.id)), "Team IDs must be unique.");
  ensure(
    unique(draft.teams.flatMap((t) => t.players.map((p) => p.id))),
    "Each player may appear in only one team.",
  );
  ensure(
    unique(draft.teams.map((t) => String(t.seed))) &&
      draft.teams.every((t) => t.seed <= draft.teams.length),
    "Seeds must use each number from one to the team count once.",
  );
  ensure(unique(draft.courts.map((c) => c.id)), "Court IDs must be unique.");
  ensure(
    unique(draft.courts.flatMap((c) => (c.netTeamId ? [c.netTeamId] : []))),
    "A team's net can belong to only one court.",
  );
  ensure(
    draft.courts.every(
      (c) =>
        !c.netTeamId ||
        draft.teams.some((t) => t.id === c.netTeamId && t.bringsNet),
    ),
    "Choose a net owner who is bringing a net.",
  );
  ensure(
    draft.config.qualifiers <= draft.teams.length &&
      ![1].includes(draft.config.qualifiers),
    "Choose zero or at least two qualifiers, up to the team count.",
  );
  ensure(
    draft.config.mode === "swiss" ||
      !["king", "pools"].includes(draft.config.mode) ||
      draft.config.qualifiers >= 2,
    "This format needs at least two playoff qualifiers.",
  );
  for (const court of draft.courts) {
    const sorted = court.windows.toSorted((a, b) =>
      (a.date + a.opens).localeCompare(b.date + b.opens),
    );
    sorted.forEach((w, i) => {
      ensure(
        !Number.isNaN(Date.parse(w.date)) &&
          Number(w.opens.slice(0, 2)) < 24 &&
          Number(w.closes.slice(0, 2)) < 24 &&
          Number(w.opens.slice(3)) < 60 &&
          Number(w.closes.slice(3)) < 60,
        "Enter a valid court date and time.",
      );
      const previous = sorted[i - 1];
      ensure(
        !previous || previous.date !== w.date || previous.closes <= w.opens,
        "Court availability windows overlap.",
      );
    });
  }
}
export function createTournament(
  id: string,
  input: unknown,
  now: string,
): Tournament {
  const draft = draftSchema.parse(input);
  validateDraft(draft);
  const t: Tournament = {
    ...draft,
    id,
    version: 0,
    nextMatchNumber: 0,
    status: "draft",
    published: false,
    pools: [],
    matches: [],
    heats: [],
    tieDecisions: [],
    playoffGenerated: false,
    withdrawals: [],
    createdAt: now,
  };
  generate(t);
  return t;
}
function generate(t: Tournament): void {
  t.matches = [];
  t.pools = [];
  t.heats = [];
  t.playoffGenerated = false;
  const ranked = t.teams
    .toSorted((a, b) => a.seed - b.seed)
    .map((team) => team.id);
  switch (t.config.mode) {
    case "single":
      elimination(t, ranked);
      t.playoffGenerated = true;
      break;
    case "double":
      elimination(t, ranked, true);
      t.playoffGenerated = true;
      break;
    case "pools":
      poolDraw(t);
      break;
    case "swiss":
      swissRound(t);
      break;
    case "king":
      kingRound(t);
      break;
  }
  resolveMatches(t);
  assignPoolReferees(t);
  assignEliminationReferees(t);
}
function administrator(actor: Actor): void {
  ensure(
    actor.role === "admin",
    "Only the organizer can do that.",
    "forbidden",
  );
}
export function mayScore(t: Tournament, match: Match, actor: Actor): boolean {
  if (actor.role === "admin") return true;
  if (actor.tournamentId !== t.id) return false;
  return match.teams.includes(actor.teamId) || match.refereeId === actor.teamId;
}
export function scoringIssue(
  t: Tournament,
  match: Match,
  now: string,
): string | null {
  if (!t.published || t.status !== "running")
    return "The tournament is not running.";
  if (match.status === "complete")
    return "A result has already been submitted. Only the organizer can correct it.";
  if (!match.teams.every(Boolean) || match.status === "waiting")
    return "Both opponents must be known before scoring.";
  if (match.needsRelease && !match.released)
    return "This match needs organizer release.";
  if (match.notBefore && Date.parse(match.notBefore) > Date.parse(now))
    return "The not-before time has not arrived.";
  return null;
}
function checkScore(
  t: Tournament,
  match: Match,
  actor: Actor,
  revision: number,
  now: string,
): void {
  ensure(
    mayScore(t, match, actor),
    "Only the playing teams, referee team or organizer can score this match.",
    "forbidden",
  );
  ensure(
    match.revision === revision,
    "The score changed on another device. Review the latest score before submitting.",
    "conflict",
  );
  const issue = scoringIssue(t, match, now);
  ensure(!issue, issue ?? "This match cannot be scored.", "conflict");
  if (match.status === "ready") {
    match.scoring = structuredClone(scoringFor(t, match));
    match.status = "live";
    match.startedAt = now;
  }
}
function complete(
  match: Match,
  sets: Pair[],
  winner: 0 | 1,
  now: string,
): void {
  match.sets = sets;
  match.points = [...required(sets.at(-1))];
  match.winnerId = required(match.teams[winner] ?? undefined);
  match.loserId = required(match.teams[1 - winner] ?? undefined);
  match.status = "complete";
  match.resolution = "played";
  match.completedAt = now;
  match.revision++;
}
export function progress(t: Tournament): void {
  resolveMatches(t);
  if (t.status !== "running") return;
  if (t.playoffGenerated) {
    delete t.advancementIssue;
    if (t.matches.every((m) => m.status === "complete")) t.status = "finished";
    return;
  }
  const next = structuredClone(t);
  delete next.advancementIssue;
  try {
    if (
      next.config.mode === "king" &&
      next.heats.every((h) => h.status === "complete")
    ) {
      if (Math.max(...next.heats.map((h) => h.round)) < next.config.heatCount)
        kingRound(next);
      else startPlayoff(next);
    } else if (
      next.matches.length &&
      next.matches.every((m) => m.status === "complete")
    ) {
      if (
        next.config.mode === "swiss" &&
        Math.max(...next.matches.map((m) => m.round)) < next.config.swissRounds
      )
        swissRound(next);
      else startPlayoff(next);
    }
    delete t.advancementIssue;
    Object.assign(t, next);
  } catch (error) {
    if (!(error instanceof RuleError)) throw error;
    t.advancementIssue = error.message;
  }
  resolveMatches(t);
}

export function correctionImpact(t: Tournament, matchId: string): string[] {
  const match = required(t.matches.find((m) => m.id === matchId));
  if (["pool", "swiss"].includes(match.stage))
    return t.matches
      .filter(
        (m) =>
          m.id !== matchId &&
          (match.stage === "pool"
            ? !m.poolId
            : m.round > match.round ||
              (t.playoffGenerated && m.stage !== "swiss")),
      )
      .map((m) => m.id);
  return descendants(t, matchId).map((m) => m.id);
}
function resetMatch(match: Match): void {
  match.status = "waiting";
  match.sets = [];
  match.points = [0, 0];
  delete match.winnerId;
  delete match.loserId;
  delete match.resolution;
  delete match.startedAt;
  delete match.completedAt;
  match.released = false;
  match.revision++;
  if (!match.poolId) delete match.courtId;
  if (match.refereeMode === "auto") delete match.refereeId;
}
export function applyCommand(
  current: Tournament,
  input: unknown,
  actor: Actor,
  now: string,
): Tournament {
  const command = commandSchema.parse(input);
  const t = structuredClone(current);
  if (actor.role === "team")
    ensure(
      actor.tournamentId === t.id,
      "This code is for another tournament.",
      "forbidden",
    );
  if (
    !["point", "finish", "next_set", "start_heat", "finish_heat"].includes(
      command.type,
    )
  )
    administrator(actor);
  switch (command.type) {
    case "save_draft": {
      ensure(
        t.status === "draft",
        "The published draw is frozen. Use result corrections or withdrawals instead.",
      );
      validateDraft(command.draft);
      Object.assign(t, command.draft);
      generate(t);
      break;
    }
    case "publish":
      ensure(t.status === "draft", "This tournament is already published.");
      t.published = true;
      t.status = "running";
      break;
    case "point": {
      const match = required(t.matches.find((m) => m.id === command.matchId));
      checkScore(t, match, actor, command.revision, now);
      const side = command.side === "a" ? 0 : 1;
      ensure(
        command.delta === -1 ||
          setWinner(match.points, scoringFor(t, match)) === null,
        "The set is won. Start the next set or submit the final result.",
      );
      const next = match.points[side] + command.delta;
      ensure(
        next >= 0 && next <= 199,
        "The score must be between zero and 199.",
      );
      match.points[side] = next;
      match.revision++;
      break;
    }
    case "next_set": {
      const match = required(t.matches.find((m) => m.id === command.matchId));
      checkScore(t, match, actor, command.revision, now);
      const rule = scoringFor(t, match);
      const winner = validateResult([match.points], { ...rule, setsToWin: 1 });
      const wins =
        match.sets.filter((set) => (set[0] > set[1] ? 0 : 1) === winner)
          .length + 1;
      ensure(
        wins < rule.setsToWin,
        "The match is won. Submit its final result.",
      );
      match.sets.push([...match.points]);
      match.points = [0, 0];
      match.revision++;
      break;
    }
    case "finish": {
      const match = required(t.matches.find((m) => m.id === command.matchId));
      checkScore(t, match, actor, command.revision, now);
      complete(
        match,
        command.sets,
        validateResult(command.sets, scoringFor(t, match)),
        now,
      );
      break;
    }
    case "correct": {
      const match = required(t.matches.find((m) => m.id === command.matchId));
      ensure(
        match.status === "complete" && match.resolution !== "bye",
        "Only a played result can be corrected.",
      );
      ensure(
        match.revision === command.revision,
        "The result has changed. Refresh before correcting it.",
        "conflict",
      );
      const winner = validateResult(command.sets, scoringFor(t, match));
      const affectsQualification = ["pool", "swiss"].includes(match.stage);
      const changesWinner = match.teams[winner] !== match.winnerId;
      if (changesWinner || affectsQualification) {
        const ids = correctionImpact(t, match.id);
        const affected = t.matches.filter((m) => ids.includes(m.id));
        ensure(
          command.resetDependents || !affected.some((m) => m.startedAt),
          "Dependent matches have started. Explicitly confirm resetting those matches first.",
          "dependent_matches",
        );
        if (affectsQualification) {
          t.matches = t.matches.filter((m) => !ids.includes(m.id));
          t.playoffGenerated = false;
        } else affected.forEach(resetMatch);
      }
      t.status = "running";
      complete(match, command.sets, winner, now);
      break;
    }
    case "release": {
      const match = required(t.matches.find((m) => m.id === command.matchId));
      ensure(match.status === "ready", "Only a ready match can be released.");
      match.released = true;
      break;
    }
    case "assign": {
      const match = required(t.matches.find((m) => m.id === command.matchId));
      ensure(match.status !== "complete", "This match is complete.");
      required(
        t.courts.find((c) => c.id === command.courtId),
        "Select an existing court.",
      );
      if (match.poolId)
        ensure(
          required(t.pools.find((p) => p.id === match.poolId)).courtId ===
            command.courtId,
          "Change the pool's court to move a pool match.",
        );
      if (command.refereeMode === "team") {
        const ref = required(
          t.teams.find((team) => team.id === command.refereeId),
          "Select a referee team.",
        );
        ensure(
          !match.teams.includes(ref.id),
          "A playing team cannot be its own assigned referee. Select self-ref instead.",
        );
        if (match.poolId)
          ensure(
            required(
              t.pools.find((p) => p.id === match.poolId),
            ).teamIds.includes(ref.id),
            "Pool referees must belong to the same pool.",
          );
        ensure(
          match.status !== "live" || !activeTeamIds(t, match.id).has(ref.id),
          "That referee team is already playing or refereeing.",
        );
      }
      ensure(
        match.status !== "live" ||
          (!t.matches.some(
            (m) =>
              m.id !== match.id &&
              m.status === "live" &&
              m.courtId === command.courtId,
          ) &&
            !t.heats.some(
              (h) => h.status === "live" && h.courtId === command.courtId,
            )),
        "That court is already in use.",
      );
      match.courtId = command.courtId;
      match.refereeMode = command.refereeMode;
      delete match.refereeSlot;
      match.refereeId =
        command.refereeMode === "team" ? command.refereeId : undefined;
      if (command.refereeMode === "auto" && match.status === "live") {
        match.refereeId = refereeCandidates(t, match)[0];
        ensure(
          match.refereeId,
          "No referee is available. Choose self-ref or assign an available team.",
        );
      }
      match.needsRelease = command.needsRelease;
      match.notBefore = command.notBefore ?? undefined;
      match.revision++;
      break;
    }
    case "pool_court": {
      const pool = required(t.pools.find((p) => p.id === command.poolId));
      required(t.courts.find((c) => c.id === command.courtId));
      ensure(
        !t.matches.some((m) => m.poolId === pool.id && m.status === "live"),
        "Finish the pool's live match before moving courts.",
      );
      pool.courtId = command.courtId;
      t.matches
        .filter((m) => m.poolId === pool.id && m.status !== "complete")
        .forEach((m) => {
          m.courtId = command.courtId;
        });
      break;
    }
    case "resolve_tie": {
      const tie = unresolvedTies(t).find((tie) => tie.key === command.key);
      ensure(
        tie,
        "This tie has changed or is already resolved. Refresh the standings.",
      );
      ensure(
        tie.teamIds.includes(command.winnerId),
        "Choose one of the teams in this tie.",
      );
      t.tieDecisions ??= [];
      t.tieDecisions.push({ key: tie.key, winnerId: command.winnerId });
      break;
    }
    case "advance": {
      if (
        t.config.mode === "swiss" &&
        Math.max(...t.matches.map((m) => m.round)) < t.config.swissRounds
      )
        swissRound(t);
      else if (
        t.config.mode === "king" &&
        Math.max(...t.heats.map((h) => h.round)) < t.config.heatCount
      )
        kingRound(t);
      else startPlayoff(t);
      break;
    }
    case "start_heat": {
      const heat = required(t.heats.find((h) => h.id === command.heatId));
      ensure(
        t.status === "running" && heat.status === "ready",
        "This heat cannot start yet.",
      );
      ensure(
        actor.role === "admin" || heat.teamIds.includes(actor.teamId),
        "Only a participating team can start this heat.",
        "forbidden",
      );
      const busy = activeTeamIds(t);
      ensure(
        !heat.teamIds.some((id) => busy.has(id)),
        "A team is already playing or refereeing.",
      );
      ensure(
        !t.heats.some((h) => h.status === "live" && h.courtId === heat.courtId),
        "The court is in use.",
      );
      ensure(
        courtOpen(
          required(t.courts.find((c) => c.id === heat.courtId)),
          now,
          t.timezone,
        ),
        "The court is outside its available hours.",
      );
      heat.status = "live";
      heat.startedAt = now;
      heat.revision++;
      break;
    }
    case "finish_heat": {
      const heat = required(t.heats.find((h) => h.id === command.heatId));
      ensure(
        actor.role === "admin" || heat.teamIds.includes(actor.teamId),
        "Only a participating team can submit this heat.",
        "forbidden",
      );
      ensure(
        heat.status === "live" && heat.revision === command.revision,
        "This heat has changed or already has a result.",
        "conflict",
      );
      ensure(
        Date.parse(now) >=
          Date.parse(heat.startedAt!) + t.config.heatMinutes * 60000,
        "The timed heat has not ended yet.",
      );
      ensure(
        Object.keys(command.totals).length === heat.teamIds.length &&
          heat.teamIds.every((id) => command.totals[id] !== undefined),
        "Enter the agreed total for every team in the heat.",
      );
      heat.totals = command.totals;
      heat.status = "complete";
      heat.completedAt = now;
      heat.revision++;
      break;
    }
    case "correct_heat": {
      const heat = required(t.heats.find((h) => h.id === command.heatId));
      ensure(
        heat.status === "complete" && heat.revision === command.revision,
        "The heat result has changed.",
        "conflict",
      );
      ensure(
        Object.keys(command.totals).length === heat.teamIds.length &&
          heat.teamIds.every((id) => command.totals[id] !== undefined),
        "Enter a total for each participating team.",
      );
      ensure(
        command.resetDependents || !t.matches.some((m) => m.startedAt),
        "The playoff has started. Confirm resetting it to correct this heat.",
        "dependent_matches",
      );
      heat.totals = command.totals;
      heat.revision++;
      if (t.playoffGenerated) {
        t.matches = [];
        t.playoffGenerated = false;
      }
      t.status = "running";
      break;
    }
    case "withdraw":
      withdraw(t, command.teamId, command.replacementId, now);
      break;
    case "retire": {
      const match = required(t.matches.find((m) => m.id === command.matchId));
      ensure(
        match.status === "live" && match.revision === command.revision,
        "This match is no longer live.",
        "conflict",
      );
      ensure(
        match.teams.includes(command.winnerId),
        "Choose one of the playing teams.",
      );
      match.winnerId = command.winnerId;
      match.loserId =
        match.teams.find((id) => id !== command.winnerId) ?? undefined;
      match.status = "complete";
      match.resolution = "retirement";
      match.completedAt = now;
      match.revision++;
      break;
    }
    case "manual_swiss": {
      ensure(
        t.config.mode === "swiss" &&
          t.matches.every((m) => m.status === "complete") &&
          !t.playoffGenerated,
        "Manual pairing is only available between Swiss rounds.",
      );
      const ids = command.pairs
        .flat()
        .filter((id): id is string => id !== null);
      const eligible = t.teams.filter((team) => !team.withdrawn);
      ensure(
        new Set(ids).size === eligible.length &&
          ids.length === eligible.length &&
          eligible.every((team) => ids.includes(team.id)),
        "Each active team must appear exactly once.",
      );
      ensure(
        command.pairs.filter((p) => p[1] === null).length <= 1,
        "Only one bye is allowed per round.",
      );
      const round = Math.max(...t.matches.map((m) => m.round)) + 1;
      ensure(round <= t.config.swissRounds, "All Swiss rounds are complete.");
      for (const [a, b] of command.pairs)
        makeMatch(
          t,
          [{ teamId: a }, b ? { teamId: b } : { bye: true }],
          "swiss",
          round,
          round,
        );
      break;
    }
    case "settings": {
      ensure(t.status !== "finished", "The tournament has finished.");
      ensure(
        command.config.mode === t.config.mode &&
          command.config.poolCount === t.config.poolCount &&
          command.config.bronze === t.config.bronze,
        "The draw format is frozen after publication.",
      );
      ensure(
        JSON.stringify(command.config.pointsTable) ===
          JSON.stringify(t.config.pointsTable),
        "The published points table is frozen.",
      );
      ensure(
        command.config.qualifiers <=
          t.teams.filter((team) => !team.withdrawn).length &&
          command.config.qualifiers !== 1,
        "Choose a valid number of qualifiers.",
      );
      ensure(
        !t.playoffGenerated ||
          command.config.qualifiers === t.config.qualifiers,
        "The playoff is already generated.",
      );
      ensure(
        command.config.heatMinutes === t.config.heatMinutes,
        "Heat duration is frozen after publication so every team plays for the same time.",
      );
      ensure(
        command.config.heatCount >= Math.max(0, ...t.heats.map((h) => h.round)),
        "The heat count cannot remove an existing round.",
      );
      ensure(
        command.config.swissRounds >=
          Math.max(
            0,
            ...t.matches.filter((m) => m.stage === "swiss").map((m) => m.round),
          ),
        "The round count cannot remove an existing round.",
      );
      ensure(
        !["king", "pools"].includes(t.config.mode) ||
          command.config.qualifiers >= 2,
        "At least two teams must qualify for the playoff.",
      );
      for (const match of t.matches.filter(
        (m) => !m.startedAt && m.status !== "complete",
      )) {
        if (
          command.config.releaseStages.includes(match.stage) !==
          t.config.releaseStages.includes(match.stage)
        ) {
          match.needsRelease = command.config.releaseStages.includes(
            match.stage,
          );
          match.released = false;
        }
        if (
          command.config.notBefore[match.stage] !==
          t.config.notBefore[match.stage]
        )
          match.notBefore = command.config.notBefore[match.stage];
      }
      t.config = command.config;
      break;
    }
  }
  progress(t);
  dispatch(t, now);
  return t;
}

function withdraw(
  t: Tournament,
  teamId: string,
  replacementId: string | undefined,
  now: string,
): void {
  ensure(
    t.status === "running",
    "Edit the draft to remove a team before publication.",
  );
  const team = required(t.teams.find((v) => v.id === teamId));
  ensure(!team.withdrawn, "This team has already withdrawn.");
  ensure(
    !t.matches.some((m) => m.status === "live" && m.teams.includes(teamId)),
    "Use retirement for a match that is already playing.",
  );
  ensure(
    !t.heats.some((h) => h.status === "live" && h.teamIds.includes(teamId)),
    "Finish the live heat before withdrawing a team.",
  );
  team.withdrawn = true;
  for (const heat of t.heats.filter(
    (h) => h.status === "ready" && h.teamIds.includes(teamId),
  )) {
    heat.teamIds = heat.teamIds.filter((id) => id !== teamId);
    heat.revision++;
    if (!heat.teamIds.length) {
      heat.status = "complete";
      heat.totals = {};
      heat.completedAt = now;
    }
  }
  const upcoming = t.matches.filter(
    (m) => m.status !== "complete" && m.teams.includes(teamId),
  );
  if (upcoming.some((m) => m.stage === "pool" || m.stage === "swiss")) {
    for (const m of upcoming) {
      m.status = "complete";
      m.resolution = "forfeit";
      m.winnerId = m.teams.find((id) => id !== teamId) ?? undefined;
      m.loserId = teamId;
      m.completedAt = now;
      m.revision++;
    }
    t.withdrawals.push({ teamId, at: now });
    return;
  }
  const lastWin = t.matches.findLast(
    (m) => m.winnerId === teamId && m.resolution !== "bye",
  );
  const next = upcoming[0];
  if (!next) {
    t.withdrawals.push({ teamId, at: now });
    return;
  }
  const replacement = replacementId
    ? required(t.teams.find((v) => v.id === replacementId))
    : undefined;
  if (replacement) {
    ensure(
      !replacement.withdrawn &&
        replacement.id !== teamId &&
        !next.teams.includes(replacement.id),
      "Choose an available eliminated team.",
    );
    const otherMatch = t.matches.find(
      (m) =>
        m.id !== next.id &&
        m.teams.includes(replacement.id) &&
        m.status !== "complete" &&
        m.stage !== "bronze",
    );
    ensure(!otherMatch, "That team is still competing in another match.");
    ensure(
      !activeTeamIds(t).has(replacement.id),
      "The replacement is already playing or refereeing.",
    );
    const bronze = t.matches.find(
      (m) => m.stage === "bronze" && m.teams.includes(replacement.id),
    );
    ensure(
      !bronze?.startedAt,
      "The replacement's bronze match has started. Resolve that result before replacing a finalist.",
    );
    if (bronze) {
      const side = bronze.teams.indexOf(replacement.id);
      bronze.slots[side] = { bye: true };
      resetMatch(bronze);
    }
    const side = next.teams.indexOf(teamId);
    next.slots[side] = { teamId: replacement.id };
    resetMatch(next);
  } else {
    next.slots[next.teams.indexOf(teamId)] = { bye: true };
    resetMatch(next);
  }
  let awardPlace = next.stage === "final" ? 2 : 3;
  if (lastWin) {
    if (lastWin.stage === "winner") {
      const semi = required(t.matches.find((m) => m.stage === "semifinal"));
      awardPlace = 2 ** (semi.round - lastWin.round + 1) + 1;
    } else {
      awardPlace =
        2 +
        t.matches.filter(
          (m) =>
            m.wave > lastWin.wave &&
            !["pool", "swiss", "winner", "bronze"].includes(m.stage) &&
            (m.status !== "complete" || !!m.loserId),
        ).length;
    }
  }
  t.withdrawals.push({
    teamId,
    replacementId,
    matchId: next.id,
    awardPlace,
    at: now,
  });
}
